import { createCollection, enroll, getCollection, updateCollection } from "../lib/store";
import { supa } from "../lib/supa";
import { t } from "../lib/i18n";
import { h, mount } from "../ui/dom";
import { navigate } from "../ui/router";
import { signedIn } from "../ui/app";
import { collectionAvatar, COLLECTION_COLORS, growingField, spinner } from "./parts";
import type { Collection, CollectionColor } from "../lib/types";

/**
 * Create and edit share one screen: the fields are identical (title, Persian
 * subtitle, description, color), and keeping them in one place means a field
 * added here never needs adding twice.
 *
 * No icon picker: the card's icon is always the title's initial letter — see
 * `collectionAvatar` in parts.ts. `color` is the only per-collection visual
 * choice, and it's not a small icon tint — it IS the card's top-box
 * background, which is what the preview below shows directly rather than
 * just a colored circle.
 *
 * `existingId` absent → create, auto-enrolling on save so a collection you
 * just made is never a dead end (matches Browse's old inline-form behaviour).
 * `existingId` present → edit; only the owner may land here.
 */
export async function renderCollectionForm(host: HTMLElement, existingId?: string): Promise<void> {
  // Creating needs a session (createCollection/enroll both require one); editing
  // is gated below by ownership, which also covers signed-out (no session → not
  // the owner). Only the create path needs an explicit redirect here.
  if (!existingId && !signedIn) {
    navigate("/signin", true);
    return;
  }

  mount(host, h("div", { class: "screen" }, spinner()));

  let existing: Collection | null = null;
  if (existingId) {
    existing = await getCollection(existingId);
    const { data } = await supa.auth.getSession();
    const isOwner = !!data.session && existing.owner_id === data.session.user.id;
    // Not owned by you: there is nothing to edit here. Land back on the
    // collection's own (read-only) page rather than showing an empty form.
    if (!isOwner) {
      navigate(`/c/${existingId}`, true);
      return;
    }
  }

  // Held here rather than read off the DOM each time: the swatch grid is a
  // button group, not a native input, so there is no single element to read
  // a "value" from.
  let color: CollectionColor | null = existing?.color ?? null;

  const titleInput = h("input", {
    type: "text",
    value: existing?.title ?? "",
    placeholder: t("collectionTitle"),
    dir: "ltr",
  });
  const titleFaInput = h("input", {
    type: "text",
    value: existing?.title_fa ?? "",
    placeholder: t("collectionTitleFa"),
    dir: "auto",
  });
  const descriptionInput = growingField(t("collectionDescription"), {
    dir: "auto",
    value: existing?.description ?? undefined,
  });
  const warning = h("div", { class: "msg", hidden: true });

  // Shows the color as what it actually is — the card's top-box background —
  // rather than a small tinted circle, so picking a swatch here previews the
  // real effect instead of a proxy for it.
  const previewSlot = h("div", { class: "form-preview" });
  const renderPreview = () => {
    const tintClass = color ? `tint-${color}` : "";
    mount(
      previewSlot,
      h("div", { class: `card-body preview-swatch ${tintClass}`.trim() },
        collectionAvatar({ title: titleInput.value, icon: null, color }),
      ),
    );
  };

  const swatchButtons = COLLECTION_COLORS.map((c) =>
    h("button", {
      class: `swatch avatar-${c}`,
      attrs: { type: "button", "aria-pressed": color === c, "aria-label": c },
      on: {
        click: () => {
          // Tapping the already-selected swatch clears it back to neutral,
          // rather than needing a separate "none" control for color.
          color = color === c ? null : c;
          refreshPickers();
        },
      },
    }),
  );
  const swatchGrid = h(
    "div",
    { class: "swatch-grid", attrs: { role: "group", "aria-label": t("collectionColor") } },
    ...swatchButtons,
  );

  function refreshPickers(): void {
    swatchButtons.forEach((btn, i) => {
      const selected = color === COLLECTION_COLORS[i];
      btn.classList.toggle("selected", selected);
      btn.setAttribute("aria-pressed", String(selected));
    });
    renderPreview();
  }

  titleInput.addEventListener("input", renderPreview);

  const save = h("button", {
    class: "primary",
    text: t("save"),
    on: {
      click: async () => {
        const name = titleInput.value.trim();
        if (!name) return titleInput.focus();

        save.disabled = true;
        try {
          if (existing) {
            await updateCollection(existing.id, {
              title: name,
              title_fa: titleFaInput.value.trim() || null,
              description: descriptionInput.value.trim() || null,
              color,
            });
            navigate(`/c/${existing.id}`);
          } else {
            const created = await createCollection({
              title: name,
              titleFa: titleFaInput.value.trim() || null,
              description: descriptionInput.value.trim() || null,
              color,
            });
            // Enroll immediately: a collection you can't study is a dead end.
            await enroll(created.id);
            navigate(`/c/${created.id}`);
          }
        } catch (error) {
          save.disabled = false;
          warning.hidden = false;
          warning.className = "msg bad";
          warning.textContent = error instanceof Error ? error.message : t("errGeneric");
        }
      },
    },
  });

  const cancel = h("button", {
    class: "ghost",
    text: t("cancel"),
    on: { click: () => navigate(existing ? `/c/${existing.id}` : "/browse") },
  });

  mount(
    host,
    h("div", { class: "screen stack" },
      h("h2", { text: existing ? t("editCollection") : t("newCollection") }),
      previewSlot,
      titleInput,
      titleFaInput,
      descriptionInput,
      h("div", { class: "stack" },
        h("span", { class: "small muted", text: t("collectionColor") }),
        swatchGrid,
      ),
      warning,
      h("div", { class: "actions" }, save, cancel),
    ),
  );

  renderPreview();
}
